import { useRouter } from 'vue-router';
import type { PersonaId } from '@/demo/etat';
import { useAuthStore } from '@/stores/auth';
import { useContexteStore } from '@/stores/contexte';
import { useDecouverteStore } from '@/stores/decouverte';

/**
 * ENTRER, CHANGER DE RÔLE, SORTIR : chaque bascule change de MONDE (vraies
 * données ↔ données fictives, ou un rôle pour un autre). Il faut alors, dans
 * cet ordre : basculer l'interrupteur, oublier le contexte chargé (barème,
 * périodes, référentiels de l'autre monde), relire l'identité — le menu et les
 * gardes de route en dépendent — puis remonter l'écran courant.
 *
 * Centralisé ici pour que le bandeau, la page de connexion, le menu profil et
 * la visite guidée basculent exactement de la même façon.
 */
export function useModeDemo() {
  const auth = useAuthStore();
  const contexte = useContexteStore();
  const decouverte = useDecouverteStore();
  const router = useRouter();

  /** Après une bascule : l'écran courant est-il encore permis au nouveau rôle ? */
  async function rester(): Promise<void> {
    const courant = router.currentRoute.value;
    const roles = courant.meta.roles;
    if (roles && !auth.hasRole(...roles)) await router.replace('/');
  }

  async function entrer(persona?: PersonaId, destination = '/decouverte'): Promise<void> {
    await decouverte.entrer(persona);
    contexte.vider();
    await auth.recharger();
    await router.push(destination);
  }

  async function changerPersona(persona: PersonaId): Promise<void> {
    if (decouverte.persona === persona && auth.isAuthenticated) return;
    await decouverte.changerPersona(persona);
    contexte.vider();
    await auth.fetchMe();
    await rester();
  }

  async function reinitialiser(): Promise<void> {
    await decouverte.reinitialiser();
    contexte.vider();
    await auth.fetchMe();
    await rester();
  }

  async function quitter(): Promise<void> {
    decouverte.quitter();
    contexte.vider();
    // Relit la configuration de connexion ET l'identité réelles (cookie SSO ou
    // compte de développement) : sans session, retour à la page de connexion.
    await auth.recharger();
    await router.replace(auth.isAuthenticated ? '/' : '/login');
  }

  return { entrer, changerPersona, reinitialiser, quitter };
}
